/**
 * Browser-built cleaned evidence packs (HTML folio / PDF / Excel / Word).
 * No npm office libs. Pack bytes never go to the API.
 */
import { buildFolioPdfBytes, buildHtmlFolio, cleanDossier, folioSheetRows } from "./local-evidence-folio.js";

const DROP_KEY = /blob|token|secret|password|cookie|api_key|recording_path|image_url|whep|authorization/i;

function clip(value, limit = 128) {
  return String(value == null ? "" : value).trim().slice(0, limit);
}

function uniq(rows) {
  const out = [];
  const seen = new Set();
  for (const raw of rows || []) {
    const key = clip(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
    if (out.length >= 32) break;
  }
  return out;
}

function looksLikePath(value) {
  const text = String(value || "");
  if (!text) return false;
  if (text.startsWith("/") || text.includes("..") || text.includes("\\")) return true;
  if (/^s3:\/\//i.test(text) || /backblaze/i.test(text)) return true;
  if (text.includes("/") && !/^(snap-|uds_|cei_|cam-|dd-|loc_)/.test(text)) return true;
  return false;
}

function listOf(value) {
  if (Array.isArray(value)) return uniq(value.map((x) => clip(x)));
  const one = clip(value);
  return one ? [one] : [];
}

/** Defense-in-depth wash. Empty lists stay empty — never invent IDs. */
export function cleanEventIndex(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const index = {
    event_id: clip(src.event_id || src.incident_id),
    team_run_id: clip(src.team_run_id, 64),
    dispatch_ids: listOf(src.dispatch_ids || src.dispatch_id),
    camera_ids: listOf(src.camera_ids || src.camera_id),
    user_ids: listOf(src.user_ids || src.user_id),
    video_ids: listOf(src.video_ids || src.session_id),
    screenshot_ids: listOf(src.screenshot_ids || src.snapshot_id),
    recording_ids: listOf(src.recording_ids),
    item_ids: listOf(src.item_ids),
    chain: Array.isArray(src.chain)
      ? src.chain.slice(0, 48).map((row) => ({
          step: clip(row && row.step, 24),
          id: clip(row && row.id, 128),
        })).filter((row) => row.step && row.id && !looksLikePath(row.id))
      : [],
    server_blob_forbidden: true,
    b2_upload: false,
    soft_invent: false,
    dossier: cleanDossier(src.dossier),
  };
  const washed = {};
  for (const [key, val] of Object.entries(index)) {
    if (DROP_KEY.test(key) && key !== "server_blob_forbidden") continue;
    if (typeof val === "string" && looksLikePath(val)) continue;
    washed[key] = val;
  }
  if (!Array.isArray(washed.chain) || !washed.chain.length) {
    washed.chain = [
      washed.event_id && { step: "event", id: washed.event_id },
      washed.team_run_id && { step: "team_run", id: washed.team_run_id },
      ...(washed.dispatch_ids || []).map((id) => ({ step: "dispatch", id })),
      ...(washed.camera_ids || []).map((id) => ({ step: "camera", id })),
      ...(washed.video_ids || []).map((id) => ({ step: "video", id })),
      ...(washed.screenshot_ids || []).map((id) => ({ step: "screenshot", id })),
      ...(washed.recording_ids || []).map((id) => ({ step: "recording", id })),
    ].filter(Boolean).slice(0, 48);
  }
  return washed;
}

export function packLines(index, extras = {}) {
  return folioSheetRows(index, extras).map((row) => row.join(": "));
}

function utf8(text) {
  return new TextEncoder().encode(String(text));
}

function crc32(bytes) {
  let c = ~0;
  for (let i = 0; i < bytes.length; i++) {
    c ^= bytes[i];
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return (~c) >>> 0;
}

function u16(n) {
  return Uint8Array.of(n & 255, (n >>> 8) & 255);
}

function u32(n) {
  return Uint8Array.of(n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255);
}

function concat(parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Store-only ZIP (no compression). Enough for xlsx/docx. */
export function zipStore(files) {
  const locals = [];
  const centrals = [];
  let offset = 0;
  for (const file of files) {
    const name = utf8(file.name);
    const data = file.bytes instanceof Uint8Array ? file.bytes : utf8(file.bytes || "");
    const crc = crc32(data);
    const local = concat([
      utf8("PK\u0003\u0004"),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(name.length),
      u16(0),
      name,
      data,
    ]);
    const central = concat([
      utf8("PK\u0001\u0002"),
      u16(20),
      u16(20),
      u16(0x0800),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(name.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      name,
    ]);
    locals.push(local);
    centrals.push(central);
    offset += local.length;
  }
  const centralDir = concat(centrals);
  const eocd = concat([
    utf8("PK\u0005\u0006"),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.length),
    u32(offset),
    u16(0),
  ]);
  return concat([...locals, centralDir, eocd]);
}

export function buildPdfBytes(index, extras) {
  return buildFolioPdfBytes(index, extras);
}

export function buildHtmlBytes(index, extras) {
  return utf8(buildHtmlFolio(index, extras));
}

function xmlEscape(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function buildXlsxBytes(index, extras) {
  const lines = folioSheetRows(index, extras);
  const rows = lines.map((line, i) => {
    const a = line[0] || "";
    const b = line[1] || "";
    const c = line[2] || "";
    return `<row r="${i + 1}">`
      + `<c r="A${i + 1}" t="inlineStr"><is><t>${xmlEscape(a)}</t></is></c>`
      + `<c r="B${i + 1}" t="inlineStr"><is><t>${xmlEscape(b)}</t></is></c>`
      + `<c r="C${i + 1}" t="inlineStr"><is><t>${xmlEscape(c)}</t></is></c>`
      + `</row>`;
  }).join("");
  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
    + `<sheetData>${rows}</sheetData></worksheet>`;
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" `
    + `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + `<sheets><sheet name="EventIndex" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
    + `</Relationships>`;
  const wbRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>`
    + `</Relationships>`;
  const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
    + `<Default Extension="xml" ContentType="application/xml"/>`
    + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
    + `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    + `</Types>`;
  return zipStore([
    { name: "[Content_Types].xml", bytes: utf8(types) },
    { name: "_rels/.rels", bytes: utf8(rels) },
    { name: "xl/workbook.xml", bytes: utf8(workbook) },
    { name: "xl/_rels/workbook.xml.rels", bytes: utf8(wbRels) },
    { name: "xl/worksheets/sheet1.xml", bytes: utf8(sheet) },
  ]);
}

export function buildDocxBytes(index, extras) {
  const paras = folioSheetRows(index, extras).map((line) => (
    `<w:p><w:r><w:t xml:space="preserve">${xmlEscape(line.join(" · "))}</w:t></w:r></w:p>`
  )).join("");
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">`
    + `<w:body>${paras}<w:sectPr/></w:body></w:document>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>`
    + `</Relationships>`;
  const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
    + `<Default Extension="xml" ContentType="application/xml"/>`
    + `<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>`
    + `</Types>`;
  return zipStore([
    { name: "[Content_Types].xml", bytes: utf8(types) },
    { name: "_rels/.rels", bytes: utf8(rels) },
    { name: "word/document.xml", bytes: utf8(document) },
  ]);
}

export const PACK_FORMATS = {
  html: { ext: "html", mime: "text/html;charset=utf-8", label: "Folio" },
  pdf: { ext: "pdf", mime: "application/pdf", label: "PDF" },
  xlsx: { ext: "xlsx", mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", label: "Excel" },
  docx: { ext: "docx", mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", label: "Word" },
};

export function buildPackBytes(format, index, extras) {
  const fmt = String(format || "html").toLowerCase();
  if (fmt === "xlsx") return buildXlsxBytes(index, extras);
  if (fmt === "docx") return buildDocxBytes(index, extras);
  if (fmt === "pdf") return buildPdfBytes(index, extras);
  return buildHtmlBytes(index, extras);
}

export function fileNameFor(format, eventId) {
  const spec = PACK_FORMATS[format] || PACK_FORMATS.pdf;
  const id = clip(eventId, 80).replace(/[^\w.-]+/g, "_") || "event";
  const prefix = spec.ext === "html" ? "folio" : "event";
  return `${prefix}-${id}.${spec.ext}`;
}
